import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TypeClassificationTable } from '../types';
import { DocumentArrowDownIcon, DocumentTextIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import FloatingHint from './FloatingHint';
import workCategories from '../data/categories.js';

// Компонент для редактируемой ячейки (текст)
const EditableCell: React.FC<{
  initialValue: any;
  onValueChange: (value: string) => void;
  isTextarea?: boolean;
}> = ({ initialValue, onValueChange, isTextarea = false }) => {
  const [value, setValue] = useState(String(initialValue || ''));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Динамически изменяем высоту textarea
      textareaRef.current.style.height = 'auto'; // Сначала сбрасываем высоту
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Устанавливаем по содержимому
    }
  }, [value]);

  const handleBlur = () => {
    onValueChange(value);
  };

  if (isTextarea) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        className="w-full px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white resize-none"
        style={{ overflow: 'hidden' }} // Прячем скроллбар
        rows={1}
      />
    );
  }

  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      className="w-full px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white"
    />
  );
};

// Компонент для ячейки с выбором
const SelectCell: React.FC<{
  initialValue: any;
  options: string[];
  onValueChange: (value: string) => void;
}> = ({ initialValue, options, onValueChange }) => {
  const [value, setValue] = useState(String(initialValue || ''));

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue(e.target.value);
    onValueChange(e.target.value);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      className="w-full px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500"
    >
      <option value="">Не выбрано</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};


interface EditorProps {
  tableData: TypeClassificationTable;
  onSave: (data: any[]) => void;
  isLoading: boolean;
}

const TypeClassificationEditor: React.FC<EditorProps> = ({
  tableData,
  onSave,
  isLoading,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [hint, setHint] = useState<{
    rowIndex: number;
    columnId: string;
    rect: DOMRect;
    value: string;
  } | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const columnHelper = createColumnHelper<any>();

  useEffect(() => {
    if (tableData) {
      // Присваиваем уникальные ID для виртуализации
      const dataWithIds = tableData.table_data.map((row, index) => ({
        ...row,
        id: index,
      }));
      setData(dataWithIds);
    } else {
      setData([]);
    }
  }, [tableData]);

  const updateData = (rowIndex: number, columnId: string, value: any) => {
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return { ...old[rowIndex], [columnId]: value };
        }
        return row;
      })
    );
    if (!isEditing) setIsEditing(true);
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!tableData?.table_data || tableData.table_data.length === 0) return [];
    
    const tableColumns = Object.keys(tableData.table_data[0]);
    
    // Фильтруем столбцы, исключая original_unique_normalized_name
    const visibleColumns = tableColumns.filter(col => 
      col.toLowerCase() !== 'original_unique_normalized_name'
    );

    return visibleColumns.map(col => {
        const upperColName = col.toUpperCase();
        const isMultilineColumn = upperColName === 'ТИП' || upperColName === 'ВИД РАБОТ';
        const isWorkTypeColumn = upperColName === 'ВИД РАБОТ';
        
        let columnSize = 200;
        if (upperColName === 'ВИД РАБОТ') {
          columnSize = 400;
        } else if (upperColName === 'ТИП') {
          columnSize = 300;
        }

        return columnHelper.accessor(row => row[col], {
            id: col,
            header: col,
            cell: ({ getValue, row, column }) => {
                const isCategoryCol = col.toLowerCase() === 'category';
                const isSubCategoryCol = col.toLowerCase() === 'subcategory';

                if (isCategoryCol || isSubCategoryCol) {
                    return (
                        <SelectCell
                            initialValue={getValue()}
                            options={isCategoryCol ? tableData.options.categories : tableData.options.subcategories}
                            onValueChange={(value) => updateData(row.index, column.id, value)}
                        />
                    );
                }

                // Для столбца "Вид работ" используем автодополнение
                if (isWorkTypeColumn) {
                    const val = String(getValue() || '');
                    return (
                        <div
                            tabIndex={0}
                            onFocus={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHint({ rowIndex: row.index, columnId: column.id, rect, value: val });
                            }}
                            className="px-2 py-1 cursor-text outline-none w-full whitespace-normal"
                        >
                            {val}
                        </div>
                    );
                }

                return (
                    <EditableCell
                        initialValue={getValue()}
                        onValueChange={(value) => updateData(row.index, column.id, value)}
                        isTextarea={isMultilineColumn}
                    />
                );
            },
            size: columnSize,
        });
    });
  }, [tableData, columnHelper]);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 60, // Увеличиваем высоту строки для многострочного текста
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0) : 0;

  const handleSave = () => {
    const cleanedData = data.map(({ id, ...row }) => row);
    onSave(cleanedData);
    setIsEditing(false);
  };

  if (!tableData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Нет данных</h3>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="border-b border-gray-200 p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Проверка таблицы типов</h3>
            <div className="flex items-center space-x-2">
              {isEditing && <span className="text-sm text-orange-600">Несохраненные изменения</span>}
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Сохранить и продолжить
              </button>
            </div>
          </div>
      </div>

      <div ref={tableContainerRef} className="flex-1 overflow-auto min-h-0">
        <table className="min-w-full divide-y divide-gray-200 border-separate" style={{ borderSpacing: 0 }}>
          <thead className="bg-gray-100 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b border-gray-300" style={{ width: header.getSize() }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 relative">
            {paddingTop > 0 && <tr style={{ height: `${paddingTop}px` }}><td colSpan={columns.length} /></tr>}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <tr key={row.id} data-index={virtualRow.index} ref={rowVirtualizer.measureElement} className="group hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-0 py-0 align-top border-b border-gray-200" style={{ width: cell.column.getSize() }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && <tr style={{ height: `${paddingBottom}px` }}><td colSpan={columns.length} /></tr>}
          </tbody>
        </table>
      </div>
      {hint && (
        <FloatingHint
          anchorRect={hint.rect}
          value={hint.value}
          categories={workCategories}
          onSelect={(cat) => {
            updateData(hint.rowIndex, hint.columnId, cat);
            setHint(null);
          }}
          onClose={() => setHint(null)}
        />
      )}
    </div>
  );
};

export default TypeClassificationEditor;
